import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import { listAgents, type AgentRow } from "../api/agents";
import { createShare } from "../api/shares";

export default function AddShare() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [hostId, setHostId] = useState("");
  const [path, setPath] = useState("");
  const [access, setAccess] = useState<"read" | "write">("write");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listAgents()
      .then((data) => {
        if (cancelled) return;
        const onlineOnly = data.results.filter((a) => a.status === "online");
        setAgents(onlineOnly);
        if (onlineOnly.length > 0) setHostId(onlineOnly[0].id);
      })
      .catch((e) => {
        if (!cancelled) setError(extractError(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Preview the name other PCs will see, derived live from the path the
  // user is typing. Backend uses the same basename rule, so what they see
  // here is what they'll get in File Explorer.
  const preview = useMemo(() => deriveFolderName(path), [path]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createShare({
        host_agent_id: hostId,
        path,
        access,
      });
      navigate("/sharing");
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <Link to="/sharing" className="text-sm text-slate-600 hover:underline">
          ← Back to shared folders
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Share a folder</h1>
        <p className="mt-2 text-slate-600">
          Pick a computer and type the folder path. Other computers in the office will see it
          in File Explorer. You can also share a whole drive by typing just the drive letter.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="host">
              Which computer has the folder?
            </label>
            {agents === null ? (
              <p className="mt-1 text-sm text-slate-500">Loading computers…</p>
            ) : agents.length === 0 ? (
              <p className="mt-1 text-sm text-amber-900">
                No computers are online right now. Make sure at least one PC is running KraetdNet.
              </p>
            ) : (
              <select
                id="host"
                value={hostId}
                onChange={(e) => setHostId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 p-3"
                required
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label || a.hostname}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="path">
              Folder or drive path on that computer
            </label>
            <input
              id="path"
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder={String.raw`C:\Documents`}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3 font-mono text-sm"
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              Type the full Windows path, like C:\Documents. To share an entire drive instead
              of one folder, type just the drive letter, like D:\
            </p>
            {isDriveRoot(path) ? (
              <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This shares the whole {path.trim().slice(0, 2).toUpperCase()} drive — every
                file and folder on it, not just one folder. Make sure that's what you mean.
              </p>
            ) : preview ? (
              <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Other computers will see this as{" "}
                <span className="font-medium text-slate-800">
                  kraetdtech – {preview}
                </span>
                {" "}in File Explorer.
              </p>
            ) : null}
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-slate-700">
              How can the office use it?
            </legend>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="access"
                  value="write"
                  checked={access === "write"}
                  onChange={() => setAccess("write")}
                />
                Read and write (everyone can edit)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="access"
                  value="read"
                  checked={access === "read"}
                  onChange={() => setAccess("read")}
                />
                Read only (everyone can open, no one can change)
              </label>
            </div>
          </fieldset>

          {error ? (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting || !hostId || !path}
            className="w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Setting up…" : "Share this folder"}
          </button>
        </form>
      </div>
    </AppShell>
  );
}

// Mirror of backend `derive_friendly_label` so the live preview matches
// what the backend will derive on submit. Kept locally to avoid an extra
// round-trip — basename derivation is deterministic enough that drift is
// unlikely.
// Matches a bare drive root — "E:", "E:\", "E:/" — with nothing after the
// separator. The backend accepts this as a valid path (New-SmbShare works
// on a drive root exactly like a subfolder), so this isn't a validation
// gate — it only decides whether to swap the normal name-preview for a
// "this shares the whole drive" warning.
const DRIVE_ROOT_RE = /^[A-Za-z]:[\\/]?$/;

function isDriveRoot(path: string): boolean {
  return DRIVE_ROOT_RE.test(path.trim());
}

function deriveFolderName(path: string): string {
  const trimmed = path.trim().replace(/[\\/]+$/g, "");
  if (!trimmed) return "";
  const parts = trimmed.split(/[\\/]+/);
  return parts[parts.length - 1] || "";
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const data = (e as { response?: { data?: Record<string, unknown> } }).response?.data;
    if (data && typeof data === "object") {
      if (typeof data.detail === "string") return data.detail;
      // Field-level DRF validation errors (e.g. `validate_path` raising
      // ValidationError) come back as `{"path": ["Use a Windows path..."]}`
      // rather than `{"detail": "..."}`. Surface the first one instead of
      // falling through to the generic message — the specific reason (bad
      // path format, bad share name) is exactly what the user needs to fix.
      for (const value of Object.values(data)) {
        if (Array.isArray(value) && typeof value[0] === "string") return value[0];
      }
    }
  }
  return "Couldn't share that folder. Check the path and try again.";
}
